import React from 'react';
import I18n from 'i18n-js';

import { inMobileOrTabletBrowser } from '@reverbdotcom/commons/src/user_context_helpers';
import { conditionSlugForUuid } from '@reverbdotcom/commons/src/condition_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { ChevronDownIcon, ChevronUpIcon } from '@reverbdotcom/cadence/icons/react';
import { CSP } from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import CSPTransactionTableContainer from './shared/csp_transaction_table_container';
import PriceGuideGraphIcon from '../../../../images/price-guide-tool/price-guide-graphs.svg';

export const TRANSACTION_TABLE_PAGE_SIZE = 7;
export const MOBILE_TRANSACTION_TABLE_PAGE_SIZE = 10;

interface IExternalProps {
  csp: CSP;
  conditionUuids: string[];
  canonicalProductIds: string[];
  ssr: boolean;
}

export default function CSPPriceGuideModuleTableContainer({
  canonicalProductIds,
  conditionUuids,
  csp,
  ssr,
}: IExternalProps) {
  const user = useUser();
  const [isTableVisible, showTable] = React.useState(false);
  const conditionSlugs = conditionUuids.map(conditionUuid => conditionSlugForUuid(conditionUuid));

  const hasModelSelected = canonicalProductIds.length === 1;
  const selectedModelId = hasModelSelected ? canonicalProductIds[0] : null;

  const hasConditionSelected = conditionSlugs.length === 1;
  const selectedConditionSlug = hasConditionSelected ? conditionSlugs[0] : null;

  const transactionTableButtonIcon = isTableVisible ? ChevronUpIcon : ChevronDownIcon;
  const baseTransactionButtonText = 'discovery.priceGuideTool.cspModule.onlyTable.transactionButton';
  const transactionButtonText = isTableVisible ? `${baseTransactionButtonText}.hide` : `${baseTransactionButtonText}.show`;

  useViewTracking({
    componentName: 'CSPPriceGuideModuleTableContainer',
    eventName: MParticleEventName.ComponentView,
    cspId: csp.id,
    cspSlug: csp.slug,
    cpId: selectedModelId,
    conditionSlug: selectedConditionSlug,
  }, true);

  return (
    <div className="csp-price-guide-module-table-container">
      <div className="csp-price-guide-module-table-container__header">
        <div className="csp-price-guide-module-table-container__header__image-and-description">
          <img
            src={PriceGuideGraphIcon}
            alt={I18n.t('discovery.priceGuideTool.cspModule.nullState.imageAlt')}
          />
          <div className="">
            {I18n.t('discovery.priceGuideTool.cspModule.onlyTable.description')}
          </div>
        </div>
        <div className="csp-price-guide-module-table-container__header__button-container">
          <RCButton
            variant="muted"
            size="small"
            onClick={() => {
              const shouldShowTable = !isTableVisible;

              if (shouldShowTable) {
                trackEvent({
                  eventName: MParticleEventName.ClickedComponent,
                  componentName: 'CSPPriceGuideModuleTableContainer',
                  cspId: csp.id,
                  cspSlug: csp.slug,
                  cpId: selectedModelId,
                  conditionSlug: selectedConditionSlug,
                });
              }

              showTable(shouldShowTable);
            }}
          >
            <RCTextWithIcon
              svgComponent={transactionTableButtonIcon}
              placement="left"
            >
              {I18n.t(transactionButtonText)}
            </RCTextWithIcon>
          </RCButton>
        </div>
      </div>

      {isTableVisible && (
        <div className="csp-price-guide-module-table-container__transactions">
          <CSPTransactionTableContainer
            csp={csp}
            canonicalProductIds={canonicalProductIds}
            conditionSlugs={conditionSlugs}
            pageSize={inMobileOrTabletBrowser(user) ? MOBILE_TRANSACTION_TABLE_PAGE_SIZE : TRANSACTION_TABLE_PAGE_SIZE}
            ssr={ssr}
          />
        </div>
      )}
    </div>
  );
}
